import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import HeaderContainer from '../../containers/HeaderContainer';
import BoardsContainer from '../../containers/BoardsContainer';
import BoardActionsContainer from '../../containers/BoardActionsContainer';
import BoardSettingsModalContainer from '../../containers/BoardSettingsModalContainer';
import ProjectSettingsModalContainer from '../../containers/ProjectSettingsModalContainer';
// FAMILY-EXT: picker проект+доска (выезжает по клику на имя проекта)
import ProjectBoardPickerContainer from '../../containers/ProjectBoardPickerContainer';

import styles from './Fixed.module.scss';

// FAMILY-EXT: иконки досок/участников/фильтров живут внутри Header.
// Состояние какой таб развёрнут — в Fixed; передаём в Header через
// ownProps (HeaderContainer прокидывает ownProps насквозь). Expand-
// панель Fixed рендерит сразу под Header, click-вне сворачивает.
function Fixed({ projectId, board, isProjectSettingsOpen }) {
  const [boardTab, setBoardTab] = useState(null);
  const [boardSettingsOpen, setBoardSettingsOpen] = useState(false);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  const handleBoardTabClick = useCallback((key) => {
    setBoardTab((cur) => (cur === key ? null : key));
  }, []);

  const openBoardSettings = useCallback(() => setBoardSettingsOpen(true), []);
  const closeBoardSettings = useCallback(() => setBoardSettingsOpen(false), []);

  useEffect(() => {
    if (!boardTab) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setBoardTab(null);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setBoardTab(null);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [boardTab]);

  // FAMILY-EXT: панель раскрыта поверх контента (wrapper position:fixed),
  // поэтому, чтобы списки канбана уезжали вниз, экспонируем высоту
  // панели в CSS-переменной --family-expand-h. Static.wrapperBoard /
  // wrapperProject подмешивают её в margin-top.
  useEffect(() => {
    if (!boardTab) {
      document.documentElement.style.setProperty('--family-expand-h', '0px');
      return undefined;
    }
    const el = panelRef.current;
    if (!el) return undefined;
    const update = () => {
      document.documentElement.style.setProperty(
        '--family-expand-h',
        `${el.offsetHeight}px`,
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty('--family-expand-h', '0px');
    };
  }, [boardTab]);

  if (!projectId) {
    return (
      <div className={styles.wrapper}>
        <HeaderContainer />
      </div>
    );
  }

  const hasBoard = board && !board.isFetching;
  // Доски всегда доступны (когда есть проект); участники/фильтры — только когда доска открыта.
  const onTabClick = (key) => {
    // picker (проект+доска) и boards-list доступны всегда (когда есть проект).
    // members/filters — только когда открыта конкретная доска.
    if (key !== 'picker' && key !== 'boards' && !hasBoard) return;
    handleBoardTabClick(key);
  };

  // FAMILY-EXT: inline-блок участников+фильтров для шапки на десктопе.
  // На мобиле Header через CSS скрывает контейнер (см. .inlineActions
  // в Header.module.scss); на десктопе он показывается, а иконки
  // members/filters прячутся (.boardTabIconCollapsible).
  const inlineActions = hasBoard ? (
    <>
      <BoardActionsContainer mode="memberships" inline />
      <BoardActionsContainer mode="filters" inline />
    </>
  ) : undefined;

  return (
    <div className={styles.wrapper} ref={wrapRef}>
      <HeaderContainer
        boardTab={boardTab}
        onBoardTabClick={onTabClick}
        currentBoardName={hasBoard ? board.name : undefined}
        canEditBoard={hasBoard}
        onBoardSettingsClick={hasBoard ? openBoardSettings : undefined}
        inlineActions={inlineActions}
      />
      {boardTab && (
        <div
          className={classNames(
            styles.expandPanel,
            // picker и boards-list видны на всех экранах; members/filters
            // — только на мобиле (на десктопе inline в шапке).
            boardTab !== 'picker' && boardTab !== 'boards' && styles.expandPanelMobileOnly,
          )}
          ref={panelRef}
        >
          {boardTab === 'picker' && (
            <ProjectBoardPickerContainer onSelect={() => setBoardTab(null)} />
          )}
          {boardTab === 'boards' && <BoardsContainer mode="list" />}
          {boardTab === 'members' && hasBoard && (
            <BoardActionsContainer mode="memberships" />
          )}
          {boardTab === 'filters' && hasBoard && (
            <BoardActionsContainer mode="filters" />
          )}
        </div>
      )}
      {boardSettingsOpen && hasBoard && (
        <BoardSettingsModalContainer
          boardId={board.id}
          name={board.name}
          onClose={closeBoardSettings}
        />
      )}
      {isProjectSettingsOpen && <ProjectSettingsModalContainer />}
    </div>
  );
}

Fixed.propTypes = {
  projectId: PropTypes.string,
  board: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  isProjectSettingsOpen: PropTypes.bool,
};

Fixed.defaultProps = {
  projectId: undefined,
  board: undefined,
  isProjectSettingsOpen: false,
};

export default Fixed;
